/**
 * @engine/input — Pen Device
 *
 * Stylus/pen input device with pressure, tilt, etc.
 */

import type { PenControl } from '../types';
import { InputDevice } from './InputDevice';
import { ButtonControl } from '../controls/ButtonControl';
import { AxisControl } from '../controls/AxisControl';
import { Vector2Control } from '../controls/Vector2Control';

/**
 * Pen/stylus input device.
 */
export class Pen extends InputDevice {
  // Buttons
  readonly tip: ButtonControl;
  readonly eraser: ButtonControl;
  readonly barrel1: ButtonControl;
  readonly barrel2: ButtonControl;

  // Position & orientation
  readonly position: Vector2Control;
  readonly tilt: Vector2Control;

  // Pressure & twist
  readonly pressure: AxisControl;
  readonly twist: AxisControl;

  /** Whether pen is in range (hovering over digitizer) */
  readonly inRange: ButtonControl;

  constructor(index: number = 0) {
    super('pen', index, 'Pen');

    // Buttons
    this.tip = new ButtonControl('tip');
    this.eraser = new ButtonControl('eraser');
    this.barrel1 = new ButtonControl('barrel1');
    this.barrel2 = new ButtonControl('barrel2');
    this.inRange = new ButtonControl('inRange');

    // Position & orientation
    this.position = new Vector2Control('position');
    this.tilt = new Vector2Control('tilt');

    // Analog
    this.pressure = new AxisControl('pressure', false); // 0..1
    this.twist = new AxisControl('twist', true); // -1..1

    // Register all
    this.registerControl(this.tip);
    this.registerControl(this.eraser);
    this.registerControl(this.barrel1);
    this.registerControl(this.barrel2);
    this.registerControl(this.inRange);
    this.registerControl(this.position);
    this.registerControl(this.tilt);
    this.registerControl(this.pressure);
    this.registerControl(this.twist);
  }

  // ============================================================================
  // State Updates
  // ============================================================================

  /**
   * Update from PointerEvent (pen type)
   */
  updateFromPointerEvent(
    event: {
      x: number;
      y: number;
      pressure: number;
      tiltX: number;
      tiltY: number;
      twist: number;
      button: number;
      buttons: number;
    },
    time: number
  ): void {
    // Position
    this.position.update({ x: event.x, y: event.y }, time);

    // Pressure
    this.pressure.update(event.pressure, time);

    // Tilt (convert from degrees to normalized -1..1)
    this.tilt.update(
      {
        x: event.tiltX / 90,
        y: event.tiltY / 90,
      },
      time
    );

    // Twist (convert from degrees to normalized -1..1)
    this.twist.update(event.twist / 180, time);

    // Buttons from pointer event
    this.tip.update((event.buttons & 1) !== 0 && event.pressure > 0, time);
    this.barrel1.update((event.buttons & 2) !== 0, time);
    this.barrel2.update((event.buttons & 4) !== 0, time);
    this.eraser.update((event.buttons & 32) !== 0, time);

    // In range
    this.inRange.update(true, time);
  }

  /**
   * Mark pen as out of range
   */
  setOutOfRange(time: number): void {
    this.inRange.update(false, time);
    this.pressure.update(0, time);
    this.tip.update(false, time);
  }
}
